import type { AnalyticsServiceProvider } from '~analytics/types/analyticsProvider';

import type { AuthUserFieldsFragment } from '~/graphql/generated/graphql';
import type { AppConfig } from '~/types/appConfig';
import type { AppTypeHeaders, ExternalToolsConfig, UseClusterCountryConfig } from '~/types/core';

import { COACH_ROUTE_INDEX } from '~/constants/routes/coach';
import { UNIFIED_ROUTE_INDEX, UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import { studentRoleFragment } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { CountryCode, OrgType, RoleContextType } from '~/graphql/generated/graphql';
import { APP_TYPE } from '~/types/appConfig';
import { COUNTRY_CODE_ALPHA_2 } from '~/types/core';
import { splitClusterPath } from '~/utils/clusters';
import { isSafeRedirectPath } from '~/utils/redirect';

export const useRequestHost = () => {
  if (import.meta.client) {
    return window.location.host;
  }

  const headers = useRequestHeaders();
  // During SSR, use x-forwarded-host to get the original client request host
  // instead of the internal Nuxt dev server host (127.0.0.1:xxxxx)
  return headers['x-forwarded-host'] || headers.host;
};

/**
 * Make a cluster-relative path (e.g. `/educators/login`) navigation-ready. Two
 * forms, applied at the navigation site:
 *
 *  - `clusterRedirectPath()` — for SPA-internal nav via `navigateTo()` /
 *    `router.{push,replace}`. On the client, Vue Router applies the cluster
 *    base, so the raw path is returned. On SSR, `navigateTo` calls
 *    `sendRedirect()` which sets the Location header verbatim (router base NOT
 *    applied), so the cluster prefix is prepended — otherwise the browser
 *    resolves the redirect against the origin and lands on the root cluster.
 *  - `clusterUrl()` — fully-qualified URL for external nav
 *    (`navigateTo(url, { external: true })`) or `window.location` assignments,
 *    where the router base never applies.
 *
 * Both touch `useCurrentCluster()` only when called, so building AppConfig
 * objects stays Nuxt-context-free (important: `usePlatformAppConfig` is also
 * invoked from `getAppTypeHeaders` inside the urql `fetchOptions` closure,
 * which fires on requests that may not have an active context).
 */
export const clusterRedirectPath = (path: string): string => {
  if (import.meta.server) {
    return `${useCurrentCluster().pathPrefix.value}${path}`;
  }
  return path;
};

/**
 * Inverse of `clusterRedirectPath`, for route-guard path comparisons. On SSR a
 * mid-chain `navigateTo` resolves through vue-router — which has no cluster
 * base — before any Location header is sent, so the guards re-run against the
 * prefixed path (`/au/login`). A guard comparing `to.path` against
 * cluster-relative paths must strip the prefix first, or its own redirect
 * target looks foreign and it redirects forever (vue-router 500s the request
 * with "Infinite redirect in navigation guard").
 */
export const clusterRelativePath = (path: string): string =>
  splitClusterPath(useCurrentCluster().pathPrefix.value, path);

export const clusterUrl = (path: string): string =>
  `${useCurrentCluster().baseUrl.value}${path}`;

export const usePlatformAppConfig = (currentPath: string): AppConfig => {
  // Legacy /schools paths still resolve to the teacher portal config so the
  // middleware chain (auth, analytics appType) treats them as portal traffic
  // until 01.cluster-product-route-protection redirects them to /educators.
  if (currentPath.startsWith('/educators') || currentPath.startsWith('/schools')) {
    return {
      appType: APP_TYPE.SCHOOLS,
      userHasRequiredRole: (user: AuthUserFieldsFragment) => {
        return user.teacherRoles.length > 0;
      },
      unAuthenticatedRedirectRoute: '/educators/login',
      dashboardRedirectRoute: '/educators/lessons',
      logoutRedirectRoute: '/educators/login',
      onErrorRedirectRoute: '/educators',
      accountRoute: '/educators/account',
      registerDisabledHelpTextKey: 'general.auth.teacherRegisterDisabledHelp',
    };
  }

  if (currentPath.startsWith('/content')) {
    return {
      appType: APP_TYPE.CONTENT,
      userHasRequiredRole: (user: AuthUserFieldsFragment) => {
        return user.adminRoles.length > 0;
      },
      unAuthenticatedRedirectRoute: '/content/login',
      dashboardRedirectRoute: '/content/dashboard',
      logoutRedirectRoute: '/content/login',
      onErrorRedirectRoute: '/content',
      accountRoute: '/content', // no account route
      registerDisabledHelpTextKey: 'general.auth.contentAdminNoRoleHelp',
    };
  }

  return {
    appType: APP_TYPE.UNIFIED,
    userHasRequiredRole: (user: AuthUserFieldsFragment) => {
      const configStore = useConfigStore();
      // On a schools cluster, app access requires a school enrolment. The roles arrive
      // already scoped to this surface's region.
      if (configStore.isSchoolsCluster) {
        return useFragment(studentRoleFragment, user.studentRoles).some(role =>
          role.contextType === RoleContextType.Org
          && role.orgType === OrgType.School,
        );
      }
      return user.studentRoles.length > 0;
    },
    unAuthenticatedRedirectRoute: UNIFIED_ROUTE_INDEX,
    dashboardRedirectRoute: COACH_ROUTE_INDEX,
    logoutRedirectRoute: UNIFIED_ROUTE_LOGIN,
    onErrorRedirectRoute: UNIFIED_ROUTE_INDEX,
    accountRoute: UNIFIED_ROUTE_INDEX,
  };
};

export const usePostLoginRedirect = () => {
  const route = useRoute();
  const { dashboardRedirectRoute } = usePlatformAppConfig(route.path);

  const triggerPostLoginRedirect = async () => {
    // A same-app return path (e.g. /start#key=… from the lesson-join prompt) wins over the
    // dashboard default. Relative paths only — never an absolute URL off the query string.
    const requested = route.query.redirect;
    const target = isSafeRedirectPath(requested)
      ? requested
      : clusterRedirectPath(dashboardRedirectRoute);
    await navigateTo(target, { replace: true });
  };

  return { triggerPostLoginRedirect };
};

export const useUrqlClient = () => {
  return useNuxtApp().$urqlClient;
};

export const useClassYears = () => {
  const configStore = useConfigStore();
  const country = configStore.publicConfig?.country;
  if (!country) {
    throw new Error('configStore.publicConfig.country is not defined');
  }

  const { t } = useI18n();
  const yearGroupLabel = computed(() => t('general.classYear'));
  const currentClassYearOptions = computed(
    () =>
      country.currentClassYearOptions.map(({ grade, year }) => ({
        label: `${yearGroupLabel.value} ${grade}`,
        value: year,
      })) || [],
  );

  const classYearToGradeMapping = computed(() => {
    const map: Map<number, number> = new Map();
    country.currentClassYearOptions.forEach(({ grade, year }) => {
      map.set(year, grade);
    });
    return map;
  });

  const getYearGroupFromClassYearText = (classYear?: number | null, isGraduated?: boolean | null) => {
    if (isGraduated) {
      return t('general.graduated');
    }

    if (!classYear) {
      return null;
    }

    return classYearToGradeMapping.value.get(classYear);
  };

  const allClassYearOptions = computed(
    () =>
      country.allClassYearOptions.map(({ grade, year }) => ({
        label: `${yearGroupLabel.value} ${grade}`,
        value: year,
      })) || [],
  );

  const classYearNotUndefinedRule = useValidation().notUndefinedRule(
    t('general.errors.optionShouldBeSelected', {
      param: t('general.formFields.grade.label'),
    }),
  );

  const gradeClassYearMapping = computed(() => {
    const map: Map<string, number | null> = new Map();
    map.set('graduated', null);

    country.currentClassYearOptions.forEach(({ year, grade }) => {
      map.set(grade.toString(), year);
    });

    return map;
  });

  return {
    getYearGroupFromClassYearText,
    classYearToGradeMapping,
    currentClassYearOptions,
    allClassYearOptions,
    yearGroupLabel,
    classYearNotUndefinedRule,
    gradeClassYearMapping,
  };
};

export const setupErrorHandling = (errorHandler: {
  logError: (error: Error, additionalContext?: object) => void;
  logWarning: (error: Error, additionalContext?: object) => void;
  logInfo: (error: Error, additionalContext?: object) => void;
}) => {
  const { host } = useCurrentCluster();
  const route = useRoute();

  const ssrHeaders = useRequestHeaders(['user-agent']);

  const globalContext = computed(() => {
    return {
      ...(ssrHeaders && ssrHeaders),
      siteDomain: host.value,
      path: route.fullPath,
    };
  });

  const logErrorMessage = (message: string, additionalContext: object = {}) => {
    const loggedError = new Error(message);
    loggedError.name = 'LoggedError';
    errorHandler.logError(loggedError, {
      ...additionalContext,
      ...globalContext.value,
    });
  };

  const logWarnMessage = (message: string, additionalContext: object = {}) => {
    const loggedError = new Error(message);
    loggedError.name = 'LoggedError';
    errorHandler.logWarning(loggedError, {
      ...additionalContext,
      ...globalContext.value,
    });
  };

  const logInfoMessage = (message: string, additionalContext: object = {}) => {
    const loggedError = new Error(message);
    loggedError.name = 'LoggedError';
    errorHandler.logInfo(loggedError, {
      ...additionalContext,
      ...globalContext.value,
    });
  };

  const logError = (error: Error, additionalContext: object = {}) => {
    errorHandler.logError(error, {
      ...additionalContext,
      ...globalContext.value,
    });
  };

  const nuxtApp = useNuxtApp();

  const readErrorDetails = (error: unknown): { statusCode?: number; message?: string } => {
    if (typeof error !== 'object' || error === null) {
      return {};
    }

    const { statusCode, message } = error as { statusCode?: unknown; message?: unknown };
    return {
      ...(typeof statusCode === 'number' && { statusCode }),
      ...(typeof message === 'string' && { message }),
    };
  };

  const isClientError = (statusCode?: number): boolean =>
    statusCode !== undefined && statusCode >= 400 && statusCode < 500;

  /**
   * 4xx log at warning, not error: bots probing a host whose cluster is dark behind a
   * release flag turn every 404 into a global hook error, and the error-page render
   * fires `app:error` a second time with the error serialised to a plain object. Only
   * 5xx and non-HTTP errors are ours to page on.
   */
  const handleGlobalHookError = (error: unknown, hook: string) => {
    const { statusCode, message } = readErrorDetails(error);
    const context = {
      hook,
      ...(statusCode !== undefined && { statusCode }),
      ...globalContext.value,
    };

    if (error instanceof Error) {
      if (isClientError(statusCode)) {
        errorHandler.logWarning(error, context);
        return;
      }

      errorHandler.logError(error, context);
      return;
    }

    if (isClientError(statusCode)) {
      const httpClientError = new Error(message ?? 'HTTP client error');
      httpClientError.name = 'HttpClientError';
      errorHandler.logWarning(httpClientError, context);
      return;
    }

    const notErrorError = new Error(
      'global hook error was not an Error object',
    );
    errorHandler.logError(notErrorError, {
      ...context,
      ...(message !== undefined && { message }),
    });
  };

  // Global error handling
  nuxtApp.hook('vue:error', (error) => {
    handleGlobalHookError(error, 'vue:error');
  });

  nuxtApp.hook('app:error', (error) => {
    handleGlobalHookError(error, 'app:error');
  });

  return { logErrorMessage, logWarnMessage, logInfoMessage, logError };
};

// Nuxt 4.5 no longer infers `$analytics` from the async plugin setup's `provide`
// (it lands as `unknown`), so this seam re-asserts the provider type for every consumer.
export const useAnalyticsService = (): AnalyticsServiceProvider => {
  return useNuxtApp().$analytics as AnalyticsServiceProvider;
};

export const useOrdinalNumber = () => {
  const { t } = useI18n();

  const configStore = useConfigStore();
  const country = configStore.publicConfig?.country;
  if (!country) {
    throw new Error('configStore.publicConfig.country is not defined');
  }

  const pluralMapping = (termKey: Intl.LDMLPluralRule) => {
    switch (termKey) {
      case 'zero':
        return t('general.numericalOrdinal.zero');
      case 'one':
        return t('general.numericalOrdinal.one');
      case 'two':
        return t('general.numericalOrdinal.two');
      case 'few':
        return t('general.numericalOrdinal.three');
      case 'many':
        return t('general.numericalOrdinal.many');
      case 'other':
        return t('general.numericalOrdinal.other');
    }
  };

  const plurals = new Intl.PluralRules(country.defaultLocale, {
    type: 'ordinal',
  });
  const getOrderNumber = (value: number) =>
    `${value}${pluralMapping(plurals.select(value))}`;

  return { getOrderNumber };
};

export const useCrossSitesConfig = () => {
  const isManageReferred = useClusterCookie<string | undefined>('isManageReferred', {
    sameSite: 'none',
    secure: true,
  });

  const setManageReferred = () => {
    isManageReferred.value = Date.now().toString();
  };

  const clear = () => {
    isManageReferred.value = undefined;
  };

  return { isManageReferred, setManageReferred, clear };
};

/**
 * App-type header helper for GraphQL/REST requests. The BE no longer reads a
 * `phoenix-cluster-domain` header — cluster context is derived from the
 * X-Cluster-Code header Caddy injects. We still send `phoenix-app-type` to
 * disambiguate unified/schools/content for app-specific behaviour.
 */
export const useAppTypeHeaders = () => {
  const getAppTypeHeaders = (path: string): AppTypeHeaders => ({
    'phoenix-app-type': usePlatformAppConfig(path).appType,
  });

  return { getAppTypeHeaders };
};

/**
 * Per-country config for external admin tooling deep links (Braze, Mixpanel).
 *
 * NOTE: these should really be environment variables, not hard-coded here — they're per-deployment
 * external-tool config that just happens to be country-stable today.
 */
const EXTERNAL_TOOLS_CONFIG: Record<CountryCode, ExternalToolsConfig> = {
  [CountryCode.Aus]: {
    brazeAppGroupId: '69703c0251d28500651ef9be',
    mixpanelProfileBaseUrl: 'https://mixpanel.com/project/3982682/view/4478517/app/profile',
  },
  [CountryCode.Usa]: {
    brazeAppGroupId: '68919a3afea22400657fdbbb',
    mixpanelProfileBaseUrl: 'https://mixpanel.com/project/3216861/view/3726929/app/profile',
  },
};

export const useClusterCountryConfig = (): UseClusterCountryConfig => {
  const configStore = useConfigStore();
  const country = configStore.publicConfig?.country;
  if (!country) {
    throw new Error('configStore.publicConfig.country is not defined');
  }

  const alpha2CountryCode = computed(() => {
    switch (country.code) {
      case CountryCode.Aus:
        return COUNTRY_CODE_ALPHA_2.AU;
      case CountryCode.Usa:
        return COUNTRY_CODE_ALPHA_2.US;
      default:
        throw new Error(`Unsupported country code: ${country.code}`);
    }
  });

  // IANA timezone the cluster's calendar day-boundaries (campaign dates, countdowns) are anchored
  // to. Served by the backend on the country (`CountryCode::timezone()`) so it stays the single
  // source.
  const clusterTimezone = computed(() => country.timezone);

  return {
    clusterCountry: country,
    clusterCountryCodeAlpha2: alpha2CountryCode,
    clusterTimezone,
    isUsa: country.code === CountryCode.Usa,
    isAus: country.code === CountryCode.Aus,
    externalToolsConfig: EXTERNAL_TOOLS_CONFIG[country.code],
  };
};
